import WebApp from "@twa-dev/sdk";

class TelegramService {
  public webApp = WebApp;

  /** BackButton handler steki — eng oxirgi ro'yxatdan o'tgan handler ishlaydi
      (ichma-ich sheet/screen'lar to'g'ri yopilishi uchun) */
  private backHandlers: (() => void)[] = [];
  private backButtonBound = false;

  constructor() {
    this.webApp.ready();
    this.webApp.expand();
    try {
      // Sheet ichida scroll qilganda app pastga surilib yopilmasligi uchun
      this.webApp.disableVerticalSwipes?.();
    } catch {
      // Eski versiyalarda mavjud emas
    }
  }

  isVersionAtLeast(version: string): boolean {
    try {
      return this.webApp.isVersionAtLeast(version);
    } catch {
      return false;
    }
  }

  /** Telegram BackButton mavjudmi (Bot API 6.1+) */
  get hasBackButton(): boolean {
    return this.isVersionAtLeast("6.1");
  }

  /**
   * BackButton'ga handler qo'shadi va tugmani ko'rsatadi.
   * Qaytgan cleanup funksiyasi handlerni olib tashlaydi; stek bo'shasa tugma yashirinadi.
   */
  onBack(handler: () => void): () => void {
    if (!this.hasBackButton) return () => {};

    this.backHandlers.push(handler);
    if (!this.backButtonBound) {
      this.webApp.BackButton.onClick(this.handleBackClick);
      this.backButtonBound = true;
    }
    this.webApp.BackButton.show();

    return () => {
      const index = this.backHandlers.lastIndexOf(handler);
      if (index !== -1) this.backHandlers.splice(index, 1);
      if (this.backHandlers.length === 0) {
        this.webApp.BackButton.hide();
      }
    };
  }

  private handleBackClick = () => {
    const handler = this.backHandlers[this.backHandlers.length - 1];
    handler?.();
  };

  getUser() {
    return this.webApp.initDataUnsafe?.user;
  }

  getUserId(): string {
    const initDataUnsafe = this.webApp.initDataUnsafe;
    const userId = initDataUnsafe?.user?.id?.toString();

    // Mock ID for development if real ID is missing
    if (!userId && import.meta.env.DEV) {
      console.log("⚠️ Using mock user ID for development");
      return "2062187869"; // Your actual ID from logs
    }

    return userId || "";
  }

  getTheme() {
    return this.webApp.colorScheme;
  }

  close() {
    this.webApp.close();
  }

  // Check if showPopup is supported (requires version 6.2+)
  private isPopupSupported(): boolean {
    const version = this.webApp.version;
    if (!version) return false;
    const [major, minor] = version.split(".").map(Number);
    return major > 6 || (major === 6 && minor >= 2);
  }

  showAlert(message: string) {
    if (this.isPopupSupported()) {
      try {
        this.webApp.showAlert(message);
      } catch {
        // Fallback to console log if showAlert fails
        console.log("Alert:", message);
      }
    } else {
      // Use browser alert as fallback for unsupported versions
      console.log("Alert:", message);
    }
  }

  showConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isPopupSupported()) {
        try {
          this.webApp.showConfirm(message, resolve);
        } catch {
          resolve(window.confirm(message));
        }
      } else {
        resolve(window.confirm(message));
      }
    });
  }

  /**
   * Haptic xarita (yagona manba):
   *  - "selection" — tab/segment almashishi
   *  - "light"     — list element tap
   *  - "medium"    — primary tugma / FAB
   *  - "heavy"     — katta delight momentlar (badge unlock)
   *  - "success" | "error" | "warning" — natija bildirishnomalari
   */
  haptic(
    type:
      | "light"
      | "medium"
      | "heavy"
      | "success"
      | "error"
      | "warning"
      | "selection" = "light",
  ) {
    try {
      if (type === "selection") {
        this.webApp.HapticFeedback?.selectionChanged();
      } else if (type === "success" || type === "error" || type === "warning") {
        this.webApp.HapticFeedback?.notificationOccurred(type);
      } else {
        this.webApp.HapticFeedback?.impactOccurred(type);
      }
    } catch {
      // Haptic not available
    }
  }

  openLink(url: string) {
    if (this.webApp.openLink) {
      this.webApp.openLink(url);
    } else {
      window.open(url, "_blank");
    }
  }

  shareUrl(url: string) {
    try {
      const shareLink = `https://t.me/share/url?url=${encodeURIComponent(url)}`;
      this.openLink(shareLink);
    } catch {
      // Sharing not available
    }
  }
}

export default new TelegramService();
